import './plan.css';
import heroImage from '../../../../../assets/images/login_side_img.jpg';
import { ToastContainer } from 'react-toastify';
import { showToast } from '../../../../../utilities/helpers/ToastHelper';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import {
	getPlanData,
	requestBusinessPlan,
	requestBusinessPlanCancel
} from '../networking';
import LegalInfoDialog from '../../../common/legal-info-dialog/LegalInfoDialog';
import PlanCompareModal from './PlanCompareModal';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

// Fallbacks used until GET /providers/me/plan resolves.
const FALLBACK_BUSINESS_PRICE_EUR_PER_MONTH = 79;
const FALLBACK_BUSINESS_PRICE_EUR_PER_SEMI_ANNUAL = 420;
const FALLBACK_BUSINESS_PRICE_EUR_PER_YEAR = 790;
const FALLBACK_BASIC_COMMISSION_PCT = 10;
const FALLBACK_BUSINESS_COMMISSION_PCT = 8;
const FALLBACK_OFFLINE_COMMISSION_PCT = 0;
const FALLBACK_ENTERPRISE_PRICE_EUR_PER_MONTH = 125;
const FALLBACK_ENTERPRISE_PRICE_EUR_PER_SEMI_ANNUAL = 665;
const FALLBACK_ENTERPRISE_PRICE_EUR_PER_YEAR = 1250;
const FALLBACK_ENTERPRISE_COMMISSION_PCT = 8;
const METRIC_PLACEHOLDER = '—';

// --- TBD placeholders — user will share final email + booking link --------
const CONTACT_EMAIL_PLACEHOLDER = 'admin@upiria.com';
const CONTACT_PHONE_PLACEHOLDER = '+30 210 2201 630';
const BOOK_CALL_URL =
	'https://outlook.office.com/book/UpiriaMeetingCalendar@upiria.com/?ismsaljsauthenabled';
const CONTACT_ANCHOR_ID = 'plan-contact';
// LegalInfoDialog document types: 1=Privacy, 2=TermsOfUsage, 3=GeneralTC,
// 4=ProviderTC, 5=ProviderCancellation.
const TERMS_DOC_PROVIDER_TC = 4;

const formatTokens = (template, tokens) =>
	Object.keys(tokens).reduce(
		(acc, key) => acc.replace(`{${key}}`, tokens[key]),
		template || ''
	);

// --- Inline SVG icons (kept local — mirrors the mockup's <defs> set) -------

const IconShield = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M12 2 4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4Z" />
		<path d="m9 12 2 2 4-4" />
	</svg>
);

const IconCheck = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="3"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m5 12 5 5L20 7" />
	</svg>
);

const IconArrowRight = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M5 12h14M13 5l7 7-7 7" />
	</svg>
);

const IconCaretRight = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m9 6 6 6-6 6" />
	</svg>
);

const IconCaretDown = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m6 9 6 6 6-6" />
	</svg>
);

const IconOffline = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="3" y="5" width="18" height="14" rx="2" />
		<path d="M3 10h18M7 15h3" />
	</svg>
);

const IconHeadset = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M3 13a9 9 0 0 1 18 0M3 13v4a2 2 0 0 0 2 2h2v-6H3M21 13v4a2 2 0 0 1-2 2h-2v-6h4" />
	</svg>
);

const IconClock = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 7v5l3 2" />
	</svg>
);

const IconStack = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="m12 2 9 5-9 5-9-5 9-5Z" />
		<path d="m3 12 9 5 9-5" />
		<path d="m3 17 9 5 9-5" />
	</svg>
);

const IconMegaphone = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M3 11v2a1 1 0 0 0 1 1h2l8 5V5L6 10H4a1 1 0 0 0-1 1Z" />
		<path d="M18 8a4 4 0 0 1 0 8" />
	</svg>
);

const IconMail = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="3" y="5" width="18" height="14" rx="2" />
		<path d="m3 7 9 6 9-6" />
	</svg>
);

const IconPhone = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L7.9 9.7a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6A2 2 0 0 1 22 16.9Z" />
	</svg>
);

const Plan = ({ info }) => {
	const dictionary = useDictionary();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [openFaq, setOpenFaq] = useState(0);
	const [planData, setPlanData] = useState(null);
	const [compareOpen, setCompareOpen] = useState(false);
	const [showTermsDialog, setShowTermsDialog] = useState(false);

	useEffect(() => {
		let cancelled = false;
		getPlanData()
			.then((response) => {
				if (!cancelled) {
					setPlanData(response?.data ?? null);
				}
			})
			.catch(() => {
				// Silent fallback to constants — page stays functional.
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const businessPlanConfig = planData?.business_plan ?? null;
	const enterprisePlanConfig = planData?.enterprise_plan ?? null;
	const kpis = planData?.kpis ?? null;

	const isSubscription =
		info?.is_subscription === true ||
		info?.is_subscription === 1 ||
		info?.is_subscription === '1';
	const isVerified =
		info?.verification_status?.value === '3' ||
		info?.verification_status?.key === 'verified';

	// Plan name from /me/plan; falls back to /me's plan, then to the
	// is_subscription boolean (subscribed-without-plan = legacy business).
	const currentPlan =
		planData?.plan ?? info?.plan ?? (isSubscription ? 'business' : 'free');
	const isEnterprise = currentPlan === 'enterprise';
	const isBusiness = isSubscription && !isEnterprise;

	const canRequest = !isSubscription && isVerified;
	const canRequestEnterprise = !isEnterprise && isVerified;

	const monthlyPriceCents =
		businessPlanConfig?.price_list?.EUR?.monthly_price;
	const semiAnnualPriceCents =
		businessPlanConfig?.price_list?.EUR?.semi_annual_price;
	const annualPriceCents =
		businessPlanConfig?.price_list?.EUR?.annual_price;
	const businessPriceMonthly = Number.isFinite(monthlyPriceCents)
		? Math.round(monthlyPriceCents / 100)
		: FALLBACK_BUSINESS_PRICE_EUR_PER_MONTH;
	const businessPriceSemiAnnual = Number.isFinite(semiAnnualPriceCents)
		? Math.round(semiAnnualPriceCents / 100)
		: FALLBACK_BUSINESS_PRICE_EUR_PER_SEMI_ANNUAL;
	const businessPriceYearly = Number.isFinite(annualPriceCents)
		? Math.round(annualPriceCents / 100)
		: FALLBACK_BUSINESS_PRICE_EUR_PER_YEAR;
	const basicCommissionPct = Number.isFinite(
		businessPlanConfig?.basic_online_commission
	)
		? businessPlanConfig.basic_online_commission
		: FALLBACK_BASIC_COMMISSION_PCT;
	const businessCommissionPct = Number.isFinite(
		businessPlanConfig?.online_commission
	)
		? businessPlanConfig.online_commission
		: FALLBACK_BUSINESS_COMMISSION_PCT;
	const offlineCommissionPct = Number.isFinite(
		businessPlanConfig?.offline_commission
	)
		? businessPlanConfig.offline_commission
		: FALLBACK_OFFLINE_COMMISSION_PCT;
	const enterpriseMonthlyPriceCents =
		enterprisePlanConfig?.price_list?.EUR?.monthly_price;
	const enterpriseSemiAnnualPriceCents =
		enterprisePlanConfig?.price_list?.EUR?.semi_annual_price;
	const enterpriseAnnualPriceCents =
		enterprisePlanConfig?.price_list?.EUR?.annual_price;
	const enterprisePriceMonthly = Number.isFinite(enterpriseMonthlyPriceCents)
		? Math.round(enterpriseMonthlyPriceCents / 100)
		: FALLBACK_ENTERPRISE_PRICE_EUR_PER_MONTH;
	const enterprisePriceSemiAnnual = Number.isFinite(
		enterpriseSemiAnnualPriceCents
	)
		? Math.round(enterpriseSemiAnnualPriceCents / 100)
		: FALLBACK_ENTERPRISE_PRICE_EUR_PER_SEMI_ANNUAL;
	const enterprisePriceYearly = Number.isFinite(enterpriseAnnualPriceCents)
		? Math.round(enterpriseAnnualPriceCents / 100)
		: FALLBACK_ENTERPRISE_PRICE_EUR_PER_YEAR;
	const enterpriseCommissionPct = Number.isFinite(
		enterprisePlanConfig?.online_commission
	)
		? enterprisePlanConfig.online_commission
		: FALLBACK_ENTERPRISE_COMMISSION_PCT;

	const semiAnnualBaseline = businessPriceMonthly * 6;
	const semiAnnualSavings = Math.max(
		0,
		semiAnnualBaseline - businessPriceSemiAnnual
	);
	const semiAnnualSavingsPct =
		semiAnnualBaseline > 0
			? Math.round((semiAnnualSavings / semiAnnualBaseline) * 100)
			: 0;

	const priceTokens = {
		price: businessPriceMonthly,
		semiPrice: businessPriceSemiAnnual,
		semiSavings: semiAnnualSavings,
		semiSavingsPct: semiAnnualSavingsPct,
		yearlyPrice: businessPriceYearly,
		basicCommission: basicCommissionPct,
		businessCommission: businessCommissionPct,
		offlineCommission: offlineCommissionPct,
		entPrice: enterprisePriceMonthly,
		entSemiPrice: enterprisePriceSemiAnnual,
		entYearlyPrice: enterprisePriceYearly,
		entCommission: enterpriseCommissionPct
	};

	const bookingsCount = Number.isFinite(kpis?.bookings_count)
		? kpis.bookings_count
		: null;
	const commissionPaidEur = Number.isFinite(kpis?.commission_paid)
		? Math.round(kpis.commission_paid / 100)
		: null;
	const walkInsCount = Number.isFinite(kpis?.offline_bookings_count)
		? kpis.offline_bookings_count
		: null;
	const memberSinceDate =
		kpis?.member_since && Number.isFinite(kpis.member_since)
			? new Date(kpis.member_since * 1000)
			: null;
	const memberSinceMonth = memberSinceDate
		? memberSinceDate.toLocaleString('en-US', { month: 'short' })
		: METRIC_PLACEHOLDER;
	const memberSinceYear = memberSinceDate
		? memberSinceDate.getFullYear().toString()
		: '';

	const subscriptionHistory = Array.isArray(
		planData?.subscription_plan?.history
	)
		? planData.subscription_plan.history
		: [];
	const openHistoryEntry = (() => {
		for (let i = subscriptionHistory.length - 1; i >= 0; i--) {
			if (subscriptionHistory[i]?.cancelled_at == null) {
				return subscriptionHistory[i];
			}
		}
		return null;
	})();
	const earliestBusinessStartTs = (() => {
		const wantedPlan = isEnterprise ? 'enterprise' : 'business';
		const starts = subscriptionHistory
			.filter(
				(e) => e?.plan === wantedPlan && Number.isFinite(e?.started_at)
			)
			.map((e) => e.started_at);
		return starts.length ? Math.min(...starts) : null;
	})();
	const businessSinceDate = earliestBusinessStartTs
		? new Date(earliestBusinessStartTs * 1000)
		: null;
	const businessSinceMonth = businessSinceDate
		? businessSinceDate.toLocaleString('en-US', { month: 'short' })
		: METRIC_PLACEHOLDER;
	const businessSinceYear = businessSinceDate
		? businessSinceDate.getFullYear().toString()
		: '';
	const renewsAtTs = openHistoryEntry?.current_period_end;
	const renewsOnFormatted =
		Number.isFinite(renewsAtTs) && renewsAtTs > 0
			? new Date(renewsAtTs * 1000).toLocaleString('en-US', {
					day: 'numeric',
					month: 'short',
					year: 'numeric'
				})
			: null;
	const renewsSuffix = renewsOnFormatted
		? formatTokens(dictionary.plan_tab_business_plan_renews_suffix, {
				renewsOn: renewsOnFormatted
			})
		: '';
	const onUpgradeClicked = async () => {
		if (isSubmitting || !canRequest) {
			return;
		}

		setIsSubmitting(true);
		try {
			await requestBusinessPlan();
			showToast({
				type: 'success',
				message: dictionary.business_plan_request_sent
			});
		} catch (error) {
			showToast({
				type: 'error',
				message:
					error?.response?.data?.message ||
					dictionary.problem_requesting_business_plan,
				className: 'toast-white-error'
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const onEnterpriseClicked = async () => {
		if (isSubmitting || !canRequestEnterprise) {
			return;
		}

		setIsSubmitting(true);
		try {
			await requestBusinessPlan('enterprise');
			showToast({
				type: 'success',
				message: dictionary.enterprise_plan_request_sent
			});
		} catch (error) {
			showToast({
				type: 'error',
				message:
					error?.response?.data?.message ||
					dictionary.problem_requesting_enterprise_plan,
				className: 'toast-white-error'
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const onDowngradeClicked = async () => {
		if (isSubmitting || !isSubscription) {
			return;
		}

		setIsSubmitting(true);
		try {
			await requestBusinessPlanCancel();
			showToast({
				type: 'success',
				message: dictionary.business_plan_cancel_request_sent
			});
		} catch (error) {
			showToast({
				type: 'error',
				message:
					error?.response?.data?.message ||
					dictionary.problem_requesting_business_plan_cancel,
				className: 'toast-white-error'
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const faqItems = [
		{ q: dictionary.plan_tab_faq_q1, a: dictionary.plan_tab_faq_a1 },
		{ q: dictionary.plan_tab_faq_q2, a: dictionary.plan_tab_faq_a2 },
		{ q: dictionary.plan_tab_faq_q3, a: dictionary.plan_tab_faq_a3 },
		{ q: dictionary.plan_tab_faq_q4, a: dictionary.plan_tab_faq_a4 }
	];

	const basicFeatures = [
		{
			name: dictionary.plan_tab_feat_online_booking,
			hint: dictionary.plan_tab_feat_online_booking_hint
		},
		{
			name: dictionary.plan_tab_feat_booking_requests,
			hint: dictionary.plan_tab_feat_booking_requests_hint
		},
		{
			name: dictionary.plan_tab_feat_visibility,
			hint: dictionary.plan_tab_feat_visibility_hint
		},
		{
			name: dictionary.plan_tab_feat_run_your_day,
			hint: dictionary.plan_tab_feat_run_your_day_hint
		},
		{
			name: dictionary.plan_tab_feat_reviews,
			hint: dictionary.plan_tab_feat_reviews_hint
		}
	];

	const enterpriseFeatures = [
		{
			name: dictionary.plan_tab_feat_white_label,
			hint: dictionary.plan_tab_feat_white_label_hint,
			tags: [{ label: dictionary.plan_tab_new_tag }]
		},
		{
			name: dictionary.plan_tab_feat_own_domain,
			hint: dictionary.plan_tab_feat_own_domain_hint
		},
		{
			name: dictionary.plan_tab_feat_dedicated_support,
			hint: dictionary.plan_tab_feat_dedicated_support_hint
		}
	];

	const businessFeatures = [
		{
			name: dictionary.plan_tab_feat_offline_booking,
			hint: formatTokens(
				dictionary.plan_tab_feat_offline_booking_hint,
				priceTokens
			),
			tags: [{ label: dictionary.plan_tab_new_tag }]
		},
		{
			name: dictionary.plan_tab_feat_distribution_control,
			hint: dictionary.plan_tab_feat_distribution_control_hint
		},
		{
			name: dictionary.plan_tab_feat_promoted_by_upiria,
			hint: dictionary.plan_tab_feat_promoted_by_upiria_hint
		},
		{
			name: dictionary.plan_tab_feat_customer_contact,
			hint: dictionary.plan_tab_feat_customer_contact_hint
		},
		{
			name: dictionary.plan_tab_feat_priority_support,
			hint: dictionary.plan_tab_feat_priority_support_hint
		}
	];

	const renderFeatureList = (features) => (
		<div className="plan-feat">
			{features.map((feat) => {
				const checkClass = feat.soon
					? 'plan-feat__check plan-feat__check--soon'
					: 'plan-feat__check';
				return (
					<div className="plan-feat__row" key={feat.name}>
						<span className={checkClass}>
							{feat.soon ? <IconClock /> : <IconCheck />}
						</span>
						<div className="plan-feat__body">
							<span className="plan-feat__name">
								{feat.name}
								{feat.tags?.map((tag) => (
									<span
										key={tag.label}
										className={
											tag.variant
												? `plan-feat__tag plan-feat__tag--${tag.variant}`
												: 'plan-feat__tag'
										}
									>
										{tag.label}
									</span>
								))}
							</span>
							<span className="plan-feat__hint">{feat.hint}</span>
						</div>
					</div>
				);
			})}
		</div>
	);

	const requestButtonLabel = isSubmitting
		? dictionary.requesting_business_plan
		: dictionary.plan_tab_upgrade_to_business;

	return (
		<div
			className="plan-tab thin-scrollbar fade-on-load"
			data-testid="plan-tab"
		>
			{/* ---- Hero / current plan banner ---- */}
			<section style={{ marginBottom: 8 }}>
				<span className="plan-section-eyebrow">
					{dictionary.plan_tab_current_plan_eyebrow}
				</span>
				<div className="plan-hero">
					<div
						className="plan-hero__img"
						style={{ backgroundImage: `url(${heroImage})` }}
					>
						<div className="plan-hero__img-text">
							<span className="plan-hero__img-eyebrow">
								{dictionary.plan_tab_hero_eyebrow}
							</span>
							<h3 className="plan-hero__img-title">
								{dictionary.plan_tab_hero_title}
							</h3>
							<p className="plan-hero__img-sub">
								{dictionary.plan_tab_hero_sub}
							</p>
						</div>
					</div>
					<div className="plan-hero__body">
						<div className="plan-hero__row">
							<div className="plan-mark">
								<IconShield />
							</div>
							<div style={{ flex: 1, minWidth: 0 }}>
								<div className="plan-hero__heading">
									<h2 className="plan-hero__plan-name">
										{isEnterprise
											? dictionary.enterprise_plan
											: isBusiness
												? dictionary.business_plan
												: dictionary.plan_tab_basic_plan_name}
									</h2>
									<span className="plan-pill-active">
										{dictionary.plan_tab_active}
									</span>
								</div>
								<p className="plan-hero__plan-line">
									{isEnterprise
										? dictionary.plan_tab_enterprise_plan_line
										: isBusiness
											? formatTokens(
													dictionary.plan_tab_business_plan_line,
													{ ...priceTokens, renewsSuffix }
												)
											: dictionary.plan_tab_basic_plan_line}
								</p>
							</div>
						</div>
						<div className="plan-hero__metrics">
							<div className="plan-metric">
								<span className="plan-metric__lbl">
									{dictionary.plan_tab_metric_last_12_months}
								</span>
								<span className="plan-metric__val">
									{bookingsCount ?? METRIC_PLACEHOLDER}
									<small>{dictionary.plan_tab_metric_bookings}</small>
								</span>
							</div>
							<div className="plan-metric">
								<span className="plan-metric__lbl">
									{isSubscription
										? dictionary.plan_tab_metric_walk_ins
										: dictionary.plan_tab_metric_commission_paid}
								</span>
								<span className="plan-metric__val">
									{isSubscription
										? (walkInsCount ?? METRIC_PLACEHOLDER)
										: commissionPaidEur != null
											? `€${commissionPaidEur}`
											: METRIC_PLACEHOLDER}
									{isSubscription ? null : (
										<small>
											{dictionary.plan_tab_metric_so_far}
										</small>
									)}
								</span>
							</div>
							<div className="plan-metric">
								<span className="plan-metric__lbl">
									{isEnterprise
										? dictionary.plan_tab_metric_on_enterprise_since
										: isBusiness
											? dictionary.plan_tab_metric_on_business_since
											: dictionary.plan_tab_metric_member_since}
								</span>
								<span className="plan-metric__val">
									{isSubscription
										? businessSinceMonth
										: memberSinceMonth}
									<small>
										{isSubscription
											? businessSinceYear
											: memberSinceYear}
									</small>
								</span>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ---- Plan comparison header ---- */}
			<div className="plan-section-head" style={{ marginTop: 36 }}>
				<div>
					<span className="plan-section-eyebrow">
						{dictionary.plan_tab_available_plans_eyebrow}
					</span>
					<h2>{dictionary.plan_tab_compare_title}</h2>
					<p>
						{dictionary.plan_tab_compare_sub_lead}{' '}
						<strong>{dictionary.plan_tab_compare_sub_emphasis}</strong>{' '}
						{dictionary.plan_tab_compare_sub_tail}
					</p>
				</div>
				<button
					type="button"
					className="plan-btn plan-btn--tertiary"
					onClick={() => setCompareOpen(true)}
				>
					{dictionary.plan_tab_see_full_feature_list}
					<IconCaretRight />
				</button>
			</div>

			<div className="plans">
				{/* ---- Basic plan card ---- */}
				<article
					className="plan-card plan-card--current"
					key="plan-card-basic"
					style={
						isSubscription ? { order: 3 } : undefined
					}
				>
					<div className="plan-card__head">
						<div className="plan-card__title-row">
							<h3 className="plan-card__name">
								{dictionary.plan_tab_basic}
							</h3>
						</div>
						<span className="plan-card__chip">
							{isSubscription
								? dictionary.plan_tab_free_forever_chip
								: dictionary.plan_tab_current_plan_chip}
						</span>
					</div>
					<p className="plan-card__commission">
						{formatTokens(
							dictionary.plan_tab_commission_10,
							priceTokens
						)}
					</p>
					<div className="plan-card__price">
						{dictionary.plan_tab_free}{' '}
						<small>{dictionary.plan_tab_forever}</small>
					</div>
					<div className="plan-card__cta">
						{isSubscription ? (
							<button
								type="button"
								className="plan-btn plan-btn--secondary"
								onClick={onDowngradeClicked}
								disabled={isSubmitting}
								data-testid="request-downgrade-button"
							>
								{isSubmitting
									? dictionary.plan_tab_requesting_downgrade
									: dictionary.plan_tab_downgrade_to_basic}
							</button>
						) : (
							<button
								type="button"
								className="plan-btn plan-btn--secondary"
								disabled
							>
								{dictionary.plan_tab_on_this_plan}
							</button>
						)}
					</div>
					<div className="plan-card__divider" />
					<p className="plan-card__incl-head">
						<b>{dictionary.plan_tab_basic_includes}</b>
					</p>
					{renderFeatureList(basicFeatures)}
				</article>

				{/* ---- Business plan card ---- */}
				<article
					className="plan-card plan-card--upgrade"
					key="plan-card-business"
					style={
						isSubscription ? { order: isBusiness ? 1 : 2 } : undefined
					}>
					<div className="plan-card__head">
						<div className="plan-card__title-row">
							<h3 className="plan-card__name">
								{dictionary.plan_tab_business}
							</h3>
						</div>
						<span className="plan-card__chip plan-card__chip--rec">
							{isBusiness ? (
								<>
									<IconCheck />
									{dictionary.plan_tab_current_plan_chip}
								</>
							) : (
								dictionary.plan_tab_recommended_chip
							)}
						</span>
					</div>
					<p className="plan-card__commission">
						{formatTokens(
							dictionary.plan_tab_commission_business,
							priceTokens
						)}
					</p>
					<div className="plan-card__price">
						{formatTokens(
							dictionary.plan_tab_price_amount,
							priceTokens
						)}
						<small>{dictionary.plan_tab_price_per_month}</small>
					</div>
					<div className="plan-card__cta">
						{isBusiness ? (
							<button
								type="button"
								className="plan-btn plan-btn--secondary"
								disabled
							>
								{dictionary.plan_tab_on_business}
							</button>
						) : isEnterprise ? (
							<a
								className="plan-btn plan-btn--secondary"
								href={`#${CONTACT_ANCHOR_ID}`}
								onClick={(e) => {
									e.preventDefault();
									document
										.getElementById(CONTACT_ANCHOR_ID)
										?.scrollIntoView({
											behavior: 'smooth',
											block: 'start'
										});
								}}
							>
								{dictionary.plan_tab_talk_to_sales}
							</a>
						) : (
							<>
								<button
									type="button"
									className="plan-btn plan-btn--primary"
									onClick={onUpgradeClicked}
									disabled={!canRequest || isSubmitting}
									data-testid="request-business-plan-button"
									title={
										!isVerified
											? dictionary.plan_tab_verify_to_upgrade
											: undefined
									}
								>
									{requestButtonLabel}
									<IconArrowRight />
								</button>
								<a
									className="plan-btn plan-btn--secondary"
									href={`#${CONTACT_ANCHOR_ID}`}
									onClick={(e) => {
										e.preventDefault();
										document
											.getElementById(CONTACT_ANCHOR_ID)
											?.scrollIntoView({
												behavior: 'smooth',
												block: 'start'
											});
									}}
								>
									{dictionary.plan_tab_talk_to_sales}
								</a>
							</>
						)}
					</div>
					<div className="plan-card__divider" />
					<p className="plan-card__incl-head">
						<b>{dictionary.plan_tab_all_from_basic}</b>
						{`, ${dictionary.plan_tab_plus}`}
					</p>
					{renderFeatureList(businessFeatures)}
				</article>

				{/* ---- Enterprise plan card ---- */}
				<article
					className="plan-card plan-card--enterprise"
					key="plan-card-enterprise"
					style={
						isSubscription
							? { order: isEnterprise ? 1 : 2 }
							: undefined
					}
				>
					<div className="plan-card__head">
						<div className="plan-card__title-row">
							<h3 className="plan-card__name">
								{dictionary.plan_tab_enterprise}
							</h3>
						</div>
						<span className="plan-card__chip plan-card__chip--ent">
							{isEnterprise ? (
								<>
									<IconCheck />
									{dictionary.plan_tab_current_plan_chip}
								</>
							) : (
								dictionary.plan_tab_white_label_chip
							)}
						</span>
					</div>
					<p className="plan-card__commission">
						{formatTokens(
							dictionary.plan_tab_enterprise_commission_line,
							priceTokens
						)}
					</p>
					<div className="plan-card__price">
						{formatTokens(dictionary.plan_tab_price_amount, {
							price: enterprisePriceMonthly
						})}
						<small>{dictionary.plan_tab_price_per_month}</small>
					</div>
					<div className="plan-card__cta">
						{isEnterprise ? (
							<button
								type="button"
								className="plan-btn plan-btn--secondary"
								disabled
							>
								{dictionary.plan_tab_on_enterprise}
							</button>
						) : (
							<>
								<button
									type="button"
									className="plan-btn plan-btn--enterprise"
									onClick={onEnterpriseClicked}
									disabled={!canRequestEnterprise || isSubmitting}
									data-testid="request-enterprise-plan-button"
									title={
										!isVerified
											? dictionary.plan_tab_verify_to_upgrade
											: undefined
									}
								>
									{isSubmitting
										? dictionary.requesting_enterprise_plan
										: dictionary.plan_tab_upgrade_to_enterprise}
									<IconArrowRight />
								</button>
								<a
									className="plan-btn plan-btn--secondary"
									href={`#${CONTACT_ANCHOR_ID}`}
									onClick={(e) => {
										e.preventDefault();
										document
											.getElementById(CONTACT_ANCHOR_ID)
											?.scrollIntoView({
												behavior: 'smooth',
												block: 'start'
											});
									}}
								>
									{dictionary.plan_tab_talk_to_sales}
								</a>
							</>
						)}
					</div>
					<div className="plan-card__divider" />
					<p className="plan-card__incl-head">
						<b>{dictionary.plan_tab_all_from_business}</b>
						{`, ${dictionary.plan_tab_plus}`}
					</p>
					{renderFeatureList(enterpriseFeatures)}
				</article>
			</div>

			{/* ---- 8. Why upgrade / Your subscription — outcome cards ---- */}
			<section className="plan-unlock">
				<div className="plan-section-head">
					<div>
						<span className="plan-section-eyebrow">
							{isSubscription
								? dictionary.plan_tab_subscriber_eyebrow
								: dictionary.plan_tab_why_upgrade_eyebrow}
						</span>
						<h2>
							{isSubscription
								? dictionary.plan_tab_subscriber_unlock_title
								: dictionary.plan_tab_unlock_title}
						</h2>
					</div>
				</div>
				<div className="plan-unlock-grid">
					<div className="plan-unlock-card">
						<div className="plan-unlock-card__icon">
							<IconOffline />
						</div>
						<h4>{dictionary.plan_tab_unlock_offline_title}</h4>
						<p>{dictionary.plan_tab_unlock_offline_text}</p>
					</div>
					<div className="plan-unlock-card">
						<div className="plan-unlock-card__icon plan-unlock-card__icon--green">
							<IconStack />
						</div>
						<h4>{dictionary.plan_tab_unlock_one_system_title}</h4>
						<p>{dictionary.plan_tab_unlock_one_system_text}</p>
					</div>
					<div className="plan-unlock-card">
						<div className="plan-unlock-card__icon plan-unlock-card__icon--purple">
							<IconMegaphone />
						</div>
						<h4>{dictionary.plan_tab_unlock_online_title}</h4>
						<p>{dictionary.plan_tab_unlock_online_text}</p>
					</div>
					<div className="plan-unlock-card">
						<div className="plan-unlock-card__icon plan-unlock-card__icon--blue">
							<IconHeadset />
						</div>
						<h4>{dictionary.plan_tab_unlock_help_title}</h4>
						<p>{dictionary.plan_tab_unlock_help_text}</p>
					</div>
				</div>
			</section>

			{/* ---- 10 + 11. FAQ + contact ---- */}
			<section className="plan-bottom">
				<div>
					<span className="plan-section-eyebrow">
						{dictionary.plan_tab_faq_eyebrow}
					</span>
					<h2 style={{ margin: '0 0 16px', font: "700 18px/24px 'Roboto'" }}>
						{dictionary.plan_tab_faq_title}
					</h2>
					<div className="plan-faq">
						{faqItems.map((item, idx) => {
							const isOpen = openFaq === idx;
							return (
								<div
									className={`plan-faq-item${isOpen ? ' is-open' : ''}`}
									key={item.q}
									onClick={() => setOpenFaq(isOpen ? -1 : idx)}
								>
									<div className="plan-faq-item__q">
										{item.q}
										<span className="plan-faq-item__caret">
											<IconCaretDown />
										</span>
									</div>
									<div className="plan-faq-item__a">{item.a}</div>
								</div>
							);
						})}
					</div>
				</div>

				<aside className="plan-contact" id={CONTACT_ANCHOR_ID}>
					<span className="plan-contact__eyebrow">
						{dictionary.plan_tab_contact_eyebrow}
					</span>
					<h3>{dictionary.plan_tab_contact_title}</h3>
					<p>{dictionary.plan_tab_contact_text}</p>
					<div className="plan-contact-info">
						<div className="plan-contact-info-row">
							<IconMail />
							<a href={`mailto:${CONTACT_EMAIL_PLACEHOLDER}`}>
								{CONTACT_EMAIL_PLACEHOLDER}
							</a>
						</div>
						<div className="plan-contact-info-row">
							<IconPhone />
							<span>{CONTACT_PHONE_PLACEHOLDER}</span>
						</div>
					</div>
					<a
						className="plan-btn plan-btn--ghost"
						href={BOOK_CALL_URL}
						target="_blank"
						rel="noopener noreferrer"
					>
						{dictionary.plan_tab_book_a_call}
						<IconArrowRight />
					</a>
				</aside>
			</section>

			{/* ---- 12. Fine print ---- */}
			<p className="plan-fine-print">
				{dictionary.plan_tab_fine_print}{' '}
				<a
					href="#"
					onClick={(e) => {
						e.preventDefault();
						setShowTermsDialog(true);
					}}
				>
					{dictionary.plan_tab_read_full_terms}
				</a>
				.
			</p>

			<PlanCompareModal
				open={compareOpen}
				onClose={() => setCompareOpen(false)}
				onRequest={onUpgradeClicked}
				onRequestEnterprise={onEnterpriseClicked}
				isSubmitting={isSubmitting}
				canRequest={canRequest}
				canRequestEnterprise={canRequestEnterprise}
				isVerified={isVerified}
				currentPlan={currentPlan}
				priceTokens={priceTokens}
			/>

			{showTermsDialog ? (
				<LegalInfoDialog
					type={TERMS_DOC_PROVIDER_TC}
					setLegalInfoDialogVisibility={setShowTermsDialog}
				/>
			) : null}

			<ToastContainer />
		</div>
	);
};

Plan.propTypes = {
	info: PropTypes.object
};

export default Plan;
